import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchItemBySlug } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function EnRejseHjemPage() {
  const { headerBgColor } = useLogoTheme();
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await fetchItemBySlug('forlob_sider', '1-1-forlob');
      if (data) setPageData(data);
    }
    loadData();
  }, []);

  const heroBadge = pageData?.hero_badge || '1:1-forløb';
  const heroOverskrift = pageData?.hero_overskrift || 'EN REJSE HJEM TIL DIG SELV';
  const heroUndertitel = pageData?.hero_undertitel || 'Et personligt 1:1-forløb for kvinder';
  const badge1 = pageData?.badge_linje_1;
  const badge2 = pageData?.badge_linje_2;
  const prisTekst = pageData?.pris || '5.500 kr.';
  const rammerOverskrift = pageData?.rammer_overskrift || 'Praktiske rammer for forløbet';
  const rammerPunkter = pageData?.rammer_punkter;
  const ctaTekst = pageData?.cta_tekst || 'Kontakt mig';
  const billedeSrc = pageData?.billede?.[0]
    ? imageUrl(pageData.billede[0], 'width=1000,quality=85,format=auto')
    : '/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/lenea-evolinee-session.webp';

  return (
    <>
      <SEO
        title="1:1-forløb: En rejse hjem til dig selv - Lenea Evolinee"
        description="Et personligt 1:1-forløb for kvinder, der længes efter forandring og ønsker at komme tættere på sig selv."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
            {heroBadge}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            {heroOverskrift}
          </h1>
          <p className="text-lg sm:text-xl text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans font-medium">
            {heroUndertitel}
          </p>
          {(badge1 || badge2) && (
            <div className="pt-4 flex flex-wrap justify-center gap-3">
              {badge1 && (
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#FAF7F2] border border-[#D8CDBF] text-xs font-semibold text-[#2E2A27] tracking-wider uppercase">
                  {badge1}
                </span>
              )}
              {badge2 && (
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#F3E6E2] border border-[#D8ADA0]/40 text-xs font-semibold text-[#864B3D] tracking-wider uppercase">
                  {badge2}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Sektion 1: Intro & Session-billede */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Tekst */}
            <div className="lg:col-span-7">
              {pageData?.indhold_html ? (
                <div
                  className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                  dangerouslySetInnerHTML={{ __html: pageData.indhold_html }}
                />
              ) : (
                <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                  <p>
                    Måske mærker du, at noget i dig længes efter forandring.
                  </p>
                  <p>
                    Du kan måske ikke helt sætte ord på hvad. Du ved bare, at du ikke længere ønsker at leve på samme måde.
                  </p>
                  <p>
                    Måske er du blevet træt af at tilsidesætte dig selv for at være der for andre.
                  </p>
                  <p>
                    Måske mærker du uro i kroppen, tankemylder eller en følelse af at være fastlåst.
                  </p>
                  <p>
                    Måske står du midt i en livsforandring og savner retning.
                  </p>
                  <p>
                    Eller måske længes du bare efter at komme tættere på dig selv.
                  </p>
                  <p className="font-serif text-xl sm:text-2xl text-[#2E2A27] font-medium pt-2">
                    Et sted, hvor du kan mærke dig selv igen.
                  </p>
                </div>
              )}
            </div>

            {/* Billede */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="rounded-arch overflow-hidden border border-[#D8CDBF] shadow-lg max-w-sm w-full aspect-3/4 bg-[#EAE3D5]">
                <img
                  src={billedeSrc}
                  alt="1:1-session hos Lenea Evolinee"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 2: Et rum, der er dit */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_2_overskrift || 'Et rum, der er dit'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_2_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_2_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                I dette 1:1-forløb skaber vi et trygt og fortroligt rum, der tager udgangspunkt i dig.
              </p>
              <p>
                Der er ikke en fast opskrift, vi begynder dér, hvor du er.
              </p>
              <p>
                Sammen undersøger vi det, der fylder, det der længes efter at blive set, og det du måske er klar til at slippe.
              </p>
              <p>
                Jeg holder rummet for dig, mens vi lytter til både kroppen, hjertet og det, der ligger under ordene.
              </p>

              <div className="p-6 sm:p-8 bg-white rounded-sm border border-[#D8CDBF] space-y-2 text-[#2E2A27] font-medium">
                <p>Her skal du ikke præstere.</p>
                <p>Du behøver ikke have svarene.</p>
                <p className="text-[#98866C]">Du må komme præcis, som du er.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 3: Fra at tilpasse dig – til at mærke dig selv */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_3_overskrift || 'Fra at tilpasse dig – til at mærke dig selv'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_3_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_3_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Et tema, der går igen hos mange kvinder, er evnen til at mærke alle andre – men glemme sig selv.
              </p>

              <div className="space-y-1.5 text-[#2E2A27] font-medium py-2">
                <p>At være den, der hjælper.</p>
                <p>Den, der støtter.</p>
                <p>Den, der får det hele til at fungere.</p>
              </div>

              <p>
                At mærke hvad andre har brug for, før man overhovedet når at spørge sig selv:
              </p>

              <p className="font-serif text-xl sm:text-2xl text-[#98866C] italic">
                Hvad har jeg brug for?
              </p>

              <p>
                I forløbet kan vi arbejde med at finde tilbage til netop den forbindelse.
              </p>
              <p>
                At mærke dine behov, dine grænser, dine længsler og din indre stemme.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Og langsomt øve dig i at vælge dig selv uden dårlig samvittighed.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 4: Vi arbejder blandt andet med */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_4_overskrift || 'Vi arbejder blandt andet med'}
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_1_titel ? '' : '❤️'}</span>
                <span>{pageData?.kort_1_titel || '❤️ Lyt til hjertet'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_1_tekst || 'Hvad fortæller din indre stemme dig? Vi arbejder med intuition, meditation og refleksion, så du kan komme tættere på det, der føles sandt for dig.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_2_titel ? '' : '🌿'}</span>
                <span>{pageData?.kort_2_titel || '🌿 Kom ned i kroppen'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_2_tekst || 'Kroppen ved ofte noget, før hovedet gør. Gennem åndedræt, kropslige øvelser og nærvær arbejder vi med at skabe kontakt til kroppen og finde mere ro.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_3_titel ? '' : '🌸'}</span>
                <span>{pageData?.kort_3_titel || '🌸 Kroppens sprog'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_3_tekst || 'Vi undersøger de signaler, du oplever i kroppen, med nysgerrighed og omsorg og ser på mulige sammenhænge mellem krop, følelser og livets oplevelser.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_4_titel ? '' : '🕊️'}</span>
                <span>{pageData?.kort_4_titel || '🕊️ Slip det, der ikke længere er dit'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_4_tekst || 'Gamle mønstre, overbevisninger og måder at være i verden på kan have været nødvendige engang – men behøver ikke følge med videre. Vi undersøger, hvad du er klar til at give slip på, og hvad der ønsker at vokse frem i stedet.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_5_titel ? '' : '✨'}</span>
                <span>{pageData?.kort_5_titel || '✨ Stå stærkere i dig selv'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_5_tekst || 'At stå ved den, du er. Dine behov, dine grænser, din sårbarhed, din styrke og din intuition – uden hele tiden at skulle forklare eller undskylde.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 5: Vi følger det, der viser sig */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_5_overskrift || 'Vi følger det, der viser sig'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_5_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_5_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Forløbet har en ramme, men ikke en fast drejebog.
              </p>
              <p>
                For det, du har brug for, kan ændre sig undervejs.
              </p>

              <div className="space-y-1.5 text-[#2E2A27] font-medium py-2">
                <p>Nogle gange har du brug for samtale.</p>
                <p>Andre gange for stilhed.</p>
                <p>Måske har kroppen brug for at blive lyttet til.</p>
                <p>Måske dukker noget op, som har brug for at blive mærket.</p>
              </div>

              <p>
                Jeg bringer mine erfaringer fra kropsterapi, Reiki healing, coaching, intuition, meditation, astrologi og mine øvrige værktøjer ind, når det giver mening for dig og din proces.
              </p>
              <p>
                Det vigtigste er ikke metoden.
              </p>
              <p className="font-serif text-xl sm:text-2xl text-[#98866C]">
                Det vigtigste er dig.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 6: En rejse hjem til dig */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_6_overskrift || 'En rejse hjem til dig'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_6_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_6_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Jeg kan ikke fortælle dig, hvem du er.
              </p>
              <p>
                Men jeg kan holde et trygt rum, hvor du får mulighed for at mærke det selv.
              </p>
              <p>
                Et rum, hvor du kan sænke paraderne.
              </p>
              <div className="space-y-1 text-[#2E2A27] font-medium py-2">
                <p>Trække vejret.</p>
                <p>Lytte.</p>
                <p>Mærke.</p>
              </div>
              <p>
                Og måske begynde at høre den stemme, der hele tiden har været der.
              </p>
              <p className="font-serif text-xl text-[#2E2A27]">
                Din egen.
              </p>
              <p>
                For mig handler en indre rejse ikke om at blive en ny version af dig selv.
              </p>
              <p>
                Det handler om at komme tættere på den, du allerede er.
              </p>
              <div className="space-y-1 font-serif text-lg text-[#98866C] py-2">
                <p>Hjem til dig.</p>
                <p>Hjem til dit hjerte.</p>
                <p>Hjem til dit lys.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 7: Afslutning & CTA */}
      <section className="py-20 bg-white border-t border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {!pageData?.indhold_html && (
            <>
              <div className="space-y-4">
                <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                  Måske er det nu, du skal vælge dig selv.
                </h2>
                <div className="w-16 h-0.5 bg-[#98866C]/60" />
              </div>

              <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Hvis du mærker en længsel eller en stille fornemmelse af, at dette kunne være noget for dig, er du velkommen til at kontakte mig.
                </p>
                <p>
                  Så tager vi en personlig og uforpligtende snak om, hvor du er, og hvad du længes efter.
                </p>
                <p>
                  Jeg glæder mig til at møde dig.
                </p>
                <div className="pt-4 font-serif text-xl text-[#2E2A27]">
                  <p>Med kærlighed og åbent hjerte</p>
                  <p className="italic text-[#98866C]">Lenea</p>
                </div>
              </div>
            </>
          )}

          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
            >
              <span>{ctaTekst}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
